import { useState } from "react";
import { Search } from "lucide-react";
import { LGoTextField } from "@/ui/branded";
import { EmptyState } from "@/ui/patterns";
import type { ReservationHorizon, ReservationListView } from "@/domain/reservation/rules";
import { unfinishedCheckoutsNote } from "@/features/reservations/held-places";
import { useReservationAnswers } from "@/features/reservations/hooks/useReservationAnswers";
import { cn } from "@/lib/cn";
import { ReservationRow } from "./ReservationRow";

interface ReservationListProps {
  view: ReservationListView;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  hasMatchInQueue: boolean;
  /** Places the sleva counts as taken that no row on this screen accounts for. */
  unfinishedCheckouts: number;
}

const horizons: { horizon: ReservationHorizon; label: string }[] = [
  { horizon: "upcoming", label: "Nadcházející" },
  { horizon: "past", label: "Uzavřené" },
];

const emptyHorizonDescription: Record<ReservationHorizon, string> = {
  upcoming: "Jakmile si někdo koupí slevový termín, uvidíte ho tady i s tím, co u rezervace vyplnil.",
  past: "Zatím tu nic neproběhlo ani nebylo zrušeno.",
};

function emptyMessage({
  horizon,
  isSearching,
  hasMatchElsewhere,
  heldPlacesNote,
}: {
  horizon: ReservationHorizon;
  isSearching: boolean;
  hasMatchElsewhere: boolean;
  heldPlacesNote: string | null;
}): { title: string; description: string } {
  if (!isSearching) {
    if (heldPlacesNote !== null) {
      return { title: "Zatím tu není žádná rezervace", description: heldPlacesNote };
    }
    return {
      title: "Zatím tu nic není",
      description: emptyHorizonDescription[horizon],
    };
  }
  if (hasMatchElsewhere) {
    return {
      title: "V této sekci nic neodpovídá",
      description: "Shodu najdete výš ve frontě nebo v druhé sekci.",
    };
  }
  return {
    title: "Nic neodpovídá hledání",
    description: "Zkuste jen příjmení nebo samotný kód rezervace.",
  };
}

export function ReservationList({
  view,
  searchTerm,
  onSearchTermChange,
  hasMatchInQueue,
  unfinishedCheckouts,
}: ReservationListProps) {
  const [horizon, setHorizon] = useState<ReservationHorizon>("upcoming");
  // Above the horizons rather than inside the row, so a detail the partner
  // opened is still open after a look at the other section and back.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(new Set());
  const { cancel } = useReservationAnswers();

  function setExpanded(reservationId: number, isExpanded: boolean) {
    setExpandedIds((expanded) => {
      const next = new Set(expanded);
      if (isExpanded) next.add(reservationId);
      else next.delete(reservationId);
      return next;
    });
  }

  const isSearching = searchTerm.trim() !== "";

  const shown = view[horizon];
  const otherHorizon = horizon === "upcoming" ? view.past : view.upcoming;
  const hasMatchElsewhere = hasMatchInQueue || otherHorizon.length > 0;

  // Only under Nadcházející: a checkout still in flight can still become a
  // reservation, so it is never an answer to what has already been closed. A
  // typed term is answered by the search alone.
  const heldPlacesNote =
    unfinishedCheckouts > 0 && horizon === "upcoming" && !isSearching
      ? unfinishedCheckoutsNote(unfinishedCheckouts)
      : null;

  const empty = emptyMessage({ horizon, isSearching, hasMatchElsewhere, heldPlacesNote });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div
          className="flex items-center gap-6 border-b border-border"
          role="group"
          aria-label="Které rezervace zobrazit"
        >
          {horizons.map(({ horizon: option, label }) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === horizon}
              onClick={() => setHorizon(option)}
              className={cn(
                "-mb-px border-b-2 pb-2 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                option === horizon
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <LGoTextField
          label="Hledat zákazníka nebo kód"
          className="w-full md:w-72"
          iconLeft={<Search className="h-4 w-4" />}
          placeholder="Např. Nováková nebo AB2CD"
          value={searchTerm}
          onChange={(event) => onSearchTermChange(event.target.value)}
        />
      </div>

      {shown.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl">
          <EmptyState title={empty.title} description={empty.description} />
        </div>
      ) : (
        <>
          {heldPlacesNote !== null && (
            <p className="text-sm text-muted-foreground">{heldPlacesNote}</p>
          )}
          <ul className="divide-y divide-border border-y border-border">
            {shown.map((reservation) => (
              <ReservationRow
                key={reservation.id}
                reservation={reservation}
                isCancelling={cancel.isPending && cancel.variables === reservation.id}
                onCancel={(reservationId, options) => cancel.mutate(reservationId, options)}
                disclosure={
                  horizon === "past"
                    ? {
                        isExpanded: expandedIds.has(reservation.id),
                        onExpandedChange: (isExpanded) =>
                          setExpanded(reservation.id, isExpanded),
                      }
                    : undefined
                }
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
